/**
 * Consent Management Module
 * Handles GDPR/Privacy compliance based on user's geographic location
 */

import Alpine from 'alpinejs';

// List of countries requiring strict prior consent (EU/EEA/UK/CH)
const STRICT_CONSENT_COUNTRIES = [
  "AT","BE","BG","HR","CY","CZ","DK","EE","FI","FR","DE",
  "GR","HU","IE","IT","LV","LT","LU","MT","NL","PL","PT",
  "RO","SK","SI","ES","SE", // EU
  "IS","LI","NO",            // EEA
  "GB",                      // UK
  "CH"                       // Switzerland
];

export interface ConsentStatus {
  status: string | ConsentPreferences;
  timestamp: string;
  country: string;
}

export interface ConsentPreferences {
  essential: boolean;
  analytics: boolean;
  marketing: boolean;
}

declare global {
  interface Window {
    USER_COUNTRY?: string;
  }
}

class ConsentManager {
  private static instance: ConsentManager;

  private constructor() {}

  static getInstance(): ConsentManager {
    if (!ConsentManager.instance) {
      ConsentManager.instance = new ConsentManager();
    }
    return ConsentManager.instance;
  }

  /**
   * Check if strict consent is required based on user's country
   */
  requiresStrictConsent(): boolean {
    const country = window.USER_COUNTRY;
    if (!country) return true; // fail-safe: require consent if unknown
    return STRICT_CONSENT_COUNTRIES.includes(country);
  }

  /**
   * Get consent status from localStorage
   */
  getConsentStatus(): ConsentStatus | null {
    try {
      const consent = localStorage.getItem('lunai_consent');
      return consent ? JSON.parse(consent) : null;
    } catch (e) {
      console.error('Error reading consent status:', e);
      return null;
    }
  }

  /**
   * Save consent status to localStorage
   */
  saveConsentStatus(status: string | ConsentPreferences): void {
    try {
      const consentData: ConsentStatus = {
        status,
        timestamp: new Date().toISOString(),
        country: window.USER_COUNTRY || 'unknown'
      };
      localStorage.setItem('lunai_consent', JSON.stringify(consentData));

      // Dispatch event for other modules
      window.dispatchEvent(new CustomEvent('consent-granted', {
        detail: typeof status === 'string'
          ? this.parseStringStatus(status)
          : status
      }));
    } catch (e) {
      console.error('Error saving consent status:', e);
    }
  }

  private parseStringStatus(status: string): ConsentPreferences {
    switch (status) {
      case 'accepted':
      case 'accepted-implied':
        return { essential: true, analytics: true, marketing: true };
      case 'essential-only':
        return { essential: true, analytics: false, marketing: false };
      default:
        return { essential: true, analytics: false, marketing: false };
    }
  }

  /**
   * Check if user has granted specific consent type
   */
  hasConsent(type: 'analytics' | 'marketing'): boolean {
    const status = this.getConsentStatus();
    if (!status) return false;

    if (typeof status.status === 'string') {
      const prefs = this.parseStringStatus(status.status);
      return prefs[type];
    }

    return (status.status as ConsentPreferences)[type] || false;
  }

  /**
   * Initialize consent system
   */
  async initialize(): Promise<void> {
    // Wait for geo detection
    await this.waitForGeoDetection();

    // Get Alpine store
    const store = Alpine.store('consent') as any;

    // Check if consent is required
    if (!this.requiresStrictConsent()) {
      // For non-strict countries, assume consent
      this.saveConsentStatus('accepted-implied');
      return;
    }

    // Check if user has already consented
    const status = this.getConsentStatus();
    if (status && status.status !== 'pending') {
      // User has already made a choice, dispatch event
      const prefs = typeof status.status === 'string'
        ? this.parseStringStatus(status.status)
        : status.status as ConsentPreferences;

      window.dispatchEvent(new CustomEvent('consent-granted', { detail: prefs }));
      return;
    }

    // Show consent banner
    store.showBanner = true;
    store.requiresConsent = true;
    store.country = window.USER_COUNTRY;
  }

  private async waitForGeoDetection(): Promise<void> {
    // Wait up to 3 seconds for geo detection
    const timeout = 3000;
    const startTime = Date.now();

    while (window.USER_COUNTRY === undefined && Date.now() - startTime < timeout) {
      await new Promise(resolve => setTimeout(resolve, 100));
    }

    if (window.USER_COUNTRY === undefined) {
      console.warn('Geo detection timeout - assuming consent required');
    }
  }

  /**
   * Revoke consent (for privacy settings page)
   */
  revokeConsent(): void {
    localStorage.removeItem('lunai_consent');
    window.location.reload();
  }
}

// Export singleton instance
export const consentManager = ConsentManager.getInstance();

// Initialize Alpine data for consent
export function initConsentAlpineData(): void {
  Alpine.store('consent', {
    // State
    showBanner: false,
    showSettings: false,
    preferences: {
      essential: true,
      analytics: true,
      marketing: true
    },
    requiresConsent: false,
    country: undefined,

    // Methods
    acceptAll() {
      consentManager.saveConsentStatus('accepted');
      this.showBanner = false;
    },

    acceptEssential() {
      consentManager.saveConsentStatus('essential-only');
      this.showBanner = false;
    },

    savePreferences() {
      consentManager.saveConsentStatus(this.preferences);
      this.showBanner = false;
      this.showSettings = false;
    }
  });

  // Initialize consent banner from template
  initConsentBanner();

  // Initialize consent manager after store is ready
  consentManager.initialize();
}

/**
 * Initialize consent banner by cloning from template
 */
function initConsentBanner(): void {
  const template = document.getElementById('consent-banner-template') as HTMLTemplateElement;
  if (!template) {
    console.error('Consent banner template not found');
    return;
  }

  const clone = template.content.cloneNode(true);
  document.body.appendChild(clone);
}